import { ApiError, isConnectivityError } from '../api/client';
import type { AiBudget, MessageUsage, QueryResponse } from './types';

/**
 * The streaming ask (docs/41-assistant-streaming.md §4.2, §4.3, §9; steward-app#2548).
 *
 * Nothing here knows about React. The provider hands in how to open the stream and how to ask
 * the buffered route, and gets back the same `QueryResponse` the buffered route returns, so
 * everything after a successful answer (thread, usage, history) stays one code path.
 */

/** No line for this long, once the response headers have arrived, means the stream failed
 *  (docs/41 §4.2: the server sends a `ping` every 10 s). */
export const STREAM_IDLE_TIMEOUT_MS = 30_000;

/** What `GET /assistant/config` tells the panel. Anything else it could say reads as off. */
export interface AssistantConfig { streaming: boolean; streamUrl?: string }

/** The buffered, non-streaming default: today's behaviour. */
export const STREAMING_OFF: AssistantConfig = { streaming: false };

/** A path under the API base that is safe to append to it: one leading slash, no second one
 *  (which would make it a protocol-relative host), no `..`, and a conservative character set.
 *  The base is prepended by the client, which also attaches the bearer token, so a value
 *  that could change the host must never pass. */
const STREAM_PATH = /^\/(?!\/)[A-Za-z0-9._~\-/]*$/;

/** Reads a `/assistant/config` answer defensively. Streaming is on only for `streaming: true`
 *  together with a usable `streamUrl`; every other shape (null, a string, a missing URL, a
 *  hostile URL) is off. */
export function parseAssistantConfig(raw: unknown): AssistantConfig {
  if (!raw || typeof raw !== 'object') return STREAMING_OFF;
  const { streaming, streamUrl } = raw as { streaming?: unknown; streamUrl?: unknown };
  if (streaming !== true) return STREAMING_OFF;
  if (typeof streamUrl !== 'string' || !STREAM_PATH.test(streamUrl) || streamUrl.includes('..')) return STREAMING_OFF;
  return { streaming: true, streamUrl };
}

/**
 * Yields one parsed JSON value per NDJSON line of `body`. Handles a line split across chunks
 * (including a split multi-byte character), blank lines, `\r\n`, and a last line with no
 * trailing newline. `signal` abandons the read. A line that is not valid JSON is skipped: a reader that threw on one
 * garbled line would turn a good answer into a failure.
 */
export async function* ndjsonLines(body: ReadableStream<Uint8Array>, signal?: AbortSignal): AsyncGenerator<unknown> {
  const reader = body.getReader();
  // Cancelling is what unparks a read waiting on a silent socket (the watchdog, Stop).
  const cancel = () => { reader.cancel().catch(() => {}); };
  if (signal?.aborted) cancel();
  else signal?.addEventListener('abort', cancel, { once: true });
  const decoder = new TextDecoder();
  let buffer = '';
  const parse = (line: string): { ok: true; value: unknown } | { ok: false } => {
    const trimmed = line.trim();
    if (!trimmed) return { ok: false };
    try { return { ok: true, value: JSON.parse(trimmed) }; } catch { return { ok: false }; }
  };
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl = buffer.indexOf('\n');
      while (nl >= 0) {
        const parsed = parse(buffer.slice(0, nl));
        buffer = buffer.slice(nl + 1);
        if (parsed.ok) yield parsed.value;
        nl = buffer.indexOf('\n');
      }
    }
    buffer += decoder.decode();
    const last = parse(buffer);
    if (last.ok) yield last.value;
  } finally {
    // Releases the connection when the consumer stops early (done, error, watchdog, Stop).
    signal?.removeEventListener('abort', cancel);
    cancel();
  }
}

/** A stream that failed AFTER it had started: an `error` line, a stall, a premature end or an
 *  unreadable `done`. Never retried (the answer is already being paid for). `display` is the
 *  server's own message from an `error` line, the same copy as today's JSON errors; absent for
 *  the failures the browser detects, which show today's generic failure copy. */
export class StreamFailure extends Error {
  constructor(message: string, readonly display?: string) {
    super(message);
    this.name = 'StreamFailure';
  }
}

/** A failure before the server answered, of a kind that says nothing about whether the
 *  server started work: the browser's own fetch rejection (offline, DNS, CORS), a gateway
 *  5xx that carries no application error code, or the stream API's own method-throttle 429.
 *  That 429 is the gateway's, sent before the function is invoked, with the body
 *  `{"message":"Too Many Requests"}` and no `code`; the throttle is shared by every caller
 *  (docs/41 §6), so one busy tenant must not cost everyone else their answer. A façade 429
 *  always carries a code (the monthly limit, an invite rate limit) and is a real refusal.
 *  A timeout is deliberately NOT here: the server may be mid-answer, and asking again would
 *  pay for the question twice. */
export function isRetryableOpenFailure(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.code !== 'UNKNOWN') return false;
    return err.status >= 500 || err.status === 429;
  }
  return err instanceof TypeError;
}

export interface AskStreamedOptions {
  /** POST to the stream URL; resolves when the response HEADERS arrive. */
  open: () => Promise<Response>;
  /** The ordinary `/assistant/query`, for the one permitted fallback. */
  buffered: () => Promise<QueryResponse>;
  /** Stop. Aborting it ends the read; the caller treats the resulting throw as inert. */
  signal: AbortSignal;
  /** The whole provisional text so far (`''` after a `reset`), and the `kind` the `start` line
   *  announced, or undefined until one has arrived. */
  onText: (provisional: string, kind: string | undefined) => void;
  idleTimeoutMs?: number;
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';

function toQueryResponse(line: Record<string, unknown>): QueryResponse | null {
  if (typeof line.answer !== 'string' || !isRecord(line.usage)) return null;
  return {
    answer: line.answer,
    usage: line.usage as unknown as AiBudget,
    ...(isRecord(line.messageUsage) ? { messageUsage: line.messageUsage as unknown as MessageUsage } : {}),
    ...(typeof line.latencyMs === 'number' ? { latencyMs: line.latencyMs } : {}),
    ...(line.kind === 'firm' ? { kind: 'firm' as const } : {}),
    ...(typeof line.threadId === 'string' ? { threadId: line.threadId } : {}),
    ...(typeof line.title === 'string' ? { title: line.title } : {}),
  };
}

/**
 * Asks over the stream and resolves with the authoritative answer from the `done` line.
 *
 * Fallback (docs/41 §9): if the stream cannot be opened for a reason that says the server never
 * started (see {@link isRetryableOpenFailure}), or it opens and ends before ANY line arrived,
 * the question is asked once on `buffered`. After any line, including `start`, there is no
 * fallback: the answer is already being produced and paid for. Real refusals (401, 403, the
 * limit message, any JSON error with a code) throw as the buffered route would.
 *
 * Throws a `StreamFailure` for an `error` line, a stream that dies, stalls or ends without
 * `done`, and whatever `open` threw for a refusal.
 */
export async function askStreamed(opts: AskStreamedOptions): Promise<QueryResponse> {
  const { open, buffered, signal, onText, idleTimeoutMs = STREAM_IDLE_TIMEOUT_MS } = opts;

  let response: Response;
  try {
    response = await open();
  } catch (err) {
    if (!signal.aborted && isRetryableOpenFailure(err)) return buffered();
    throw err;
  }
  if (!response.body) return buffered();

  let sawLine = false;
  let text = '';
  let kind: string | undefined;
  const idle = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = () => {
    clearTimeout(timer);
    timer = setTimeout(() => idle.abort(), idleTimeoutMs);
  };
  // Resolves when the watchdog fires or Stop is pressed, so a read parked on a silent socket
  // is abandoned rather than awaited.
  let onStop = () => {};
  const interrupted = new Promise<'idle' | 'stop'>((resolve) => {
    idle.signal.addEventListener('abort', () => resolve('idle'), { once: true });
    onStop = () => resolve('stop');
    if (signal.aborted) resolve('stop');
    else signal.addEventListener('abort', onStop, { once: true });
  });

  const release = new AbortController();
  const lines = ndjsonLines(response.body, release.signal);
  arm();
  try {
    for (;;) {
      const next = await Promise.race([lines.next(), interrupted]);
      if (next === 'stop') throw new DOMException('Stopped.', 'AbortError');
      if (next === 'idle') throw new StreamFailure('The stream went quiet.');
      if (next.done) break;
      sawLine = true;
      arm();
      const line = next.value;
      if (!isRecord(line)) continue;
      switch (line.type) {
        case 'start':
          if (typeof line.kind === 'string') kind = line.kind;
          break;
        case 'delta':
          if (typeof line.text === 'string') { text += line.text; onText(text, kind); }
          break;
        case 'reset':
          text = '';
          onText('', kind);
          break;
        case 'done': {
          const res = toQueryResponse(line);
          if (!res) throw new StreamFailure('The stream ended with an unreadable answer.');
          return res;
        }
        case 'error':
          throw new StreamFailure(
            `The stream reported an error (${typeof line.code === 'string' ? line.code : 'no code'}).`,
            typeof line.message === 'string' && line.message ? line.message : undefined,
          );
        default:
          // status, ping and any type a newer server adds: nothing to show.
          break;
      }
    }
  } catch (err) {
    // The connection dropped before a single line: nothing was started that we know of.
    if (!sawLine && !signal.aborted && !(err instanceof StreamFailure) && (isConnectivityError(err) || err instanceof TypeError)) {
      return buffered();
    }
    throw err;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', onStop);
    release.abort();
    // Aborting unparks any pending read, so this settles; it is not awaited.
    void lines.return(undefined).catch(() => {});
  }
  // The body ended with neither `done` nor `error`.
  if (!sawLine && !signal.aborted) return buffered();
  throw new StreamFailure('The stream ended before an answer arrived.');
}
